import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatDistanceToNow } from 'date-fns';
import {
  Box, Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, alpha, useTheme,
} from '@mui/material';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import GroupsIcon from '@mui/icons-material/Groups';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import { InferencesIcon, ModelsIcon, DeploymentsIcon } from '../global/CustomIcons';

import { KPICard, Surface, StatusDot, formatNumber } from '../dashboard/components/DashboardPrimitives';
import { fetchRequest as fetchLeaderboard } from '../../redux/actions/discovery/fleetLeaderboard';
import { useDisplayNames } from '../../utils/displayNames';

const STATE_TONE = {
  done: 'success',
  active: 'primary',
  review: 'warning',
  blocked: 'warning',
  failed: 'error',
  off: 'neutral',
  pending: 'neutral',
};

const STATE_LABEL = {
  done: 'Done',
  active: 'In progress',
  review: 'Needs you',
  blocked: 'Blocked',
  failed: 'Failed',
  off: 'Off',
  pending: 'Waiting',
};

const Headline = ({ children }) => (
  <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
    {children}
  </Box>
);

const safeRelative = (d) => {
  try { if (!d) return null; return `${formatDistanceToNow(new Date(d))} ago`; }
  catch (_e) { return null; }
};

const nextRunLabel = (d) => {
  try {
    if (!d) return null;
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return null;
    if (dt.getTime() - Date.now() <= 1000) return 'Due now';
    return `in ${formatDistanceToNow(dt)}`;
  } catch (_e) { return null; }
};

const fmtGrowth = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const pct = n * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
};

const fmtRisk = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${Math.round(n * 100)}%`;
};

/**
 * Fleet Overview dashboard — same KPI language as a signal goal, with the
 * leaderboard standing in for the forecast preview.
 */
const FleetDashboard = ({ goalId, goal, activity, navigate }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const nameOf = useDisplayNames();
  const stats = activity?.stats || {};
  const phases = activity?.phases || [];
  const phaseState = (id) => phases.find((p) => p.id === id)?.state || 'pending';

  const drivers = stats.drivers || {};
  const model = stats.model || {};
  const serving = stats.serving || {};
  const fleet = stats.fleet || goal?.fleet || {};

  const go = (seg) => () => navigate(`/goals/${goalId}/${seg}`);

  const driversState = phaseState('discover');
  const modelStateKey = model.state || phaseState('fit');
  const deployLive = Boolean(stats.deploy?.live);
  const lastScored = safeRelative(serving.last_inference_at);
  const members = fleet.member_count || goal?.fleet?.member_count || 0;

  const board = useSelector((s) => s.discovery_fleet_leaderboard);
  const rows = (board?.response?.rows || []).slice(0, 8);
  const isRisk = goal?.goal_type === 'rank_by_risk';

  useEffect(() => {
    if (goalId && deployLive) {
      dispatch(fetchLeaderboard({ goalId, offset: 0, limit: 8 }));
    }
  }, [dispatch, goalId, deployLive]);

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        <KPICard
          tone={STATE_TONE[driversState] || 'neutral'}
          icon={<BubbleChartIcon sx={{ fontSize: 18 }} />}
          label="Drivers"
          primary={(
            <Headline>
              {formatNumber(drivers.validated ?? 0)}
              {drivers.exploratory ? (
                <span style={{ opacity: 0.5, fontSize: '0.5em', marginLeft: 6 }}>
                  +{drivers.exploratory}
                </span>
              ) : null}
            </Headline>
          )}
          subtitle={drivers.validated ? 'validated' : 'none yet'}
          onShortcut={go('drivers')}
          shortcutTitle="Open Driver Discovery"
        />

        <KPICard
          tone={STATE_TONE[modelStateKey] || 'neutral'}
          icon={<ModelsIcon sx={{ fontSize: 18 }} />}
          label="Model"
          primary={<Headline>{model.label || (modelStateKey === 'active' ? 'Racing…' : '—')}</Headline>}
          subtitle={STATE_LABEL[modelStateKey] || 'Awaiting bake-off'}
          onShortcut={go('models')}
          shortcutTitle="Open Models"
        />

        <KPICard
          tone={members > 0 ? 'success' : 'neutral'}
          icon={<GroupsIcon sx={{ fontSize: 18 }} />}
          label="Members"
          primary={<Headline>{formatNumber(members)}</Headline>}
          subtitle={lastScored ? `scored ${lastScored}` : (deployLive ? 'awaiting first score' : 'after bake-off')}
          onShortcut={go('forecast')}
          shortcutTitle="Open leaderboard"
        />
      </Box>

      {deployLive && (
        <Surface sx={{ p: 2.5 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box sx={{
                width: 28, height: 28, borderRadius: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: 'primary.main',
              }}>
                <InferencesIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="overline" sx={{ fontSize: 10, letterSpacing: 1, fontWeight: 600, color: 'text.secondary' }}>
                  Leaderboard
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {isRisk
                    ? 'Chance each member breaches its threshold over the horizon'
                    : 'Predicted change over the horizon vs each member’s recent level'}
                </Typography>
              </Box>
            </Stack>
            <Typography
              variant="caption"
              onClick={go('forecast')}
              sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
            >
              Open
            </Typography>
          </Stack>
          {rows.length ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={48}>#</TableCell>
                  <TableCell>Member</TableCell>
                  <TableCell align="right">{isRisk ? 'Breach risk' : 'Predicted growth'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.member_key} hover>
                    <TableCell>
                      <Typography variant="caption" fontWeight={700}>{r.rank}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>{nameOf(r.member_key)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: isRisk
                          ? undefined
                          : (Number(r.score) > 0 ? 'success.main' : Number(r.score) < 0 ? 'error.main' : undefined) }}
                      >
                        {isRisk ? fmtRisk(r.score) : fmtGrowth(r.score)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {board?.isLoading ? 'Loading the board…' : 'No scores yet — they appear here after the first run.'}
            </Typography>
          )}
        </Surface>
      )}

      <Surface sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{
              width: 28, height: 28, borderRadius: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: alpha(deployLive ? theme.palette.success.main : theme.palette.text.secondary, 0.12),
              color: deployLive ? 'success.main' : 'text.secondary',
            }}>
              <DeploymentsIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography variant="overline" sx={{ fontSize: 10, letterSpacing: 1, fontWeight: 600, color: 'text.secondary' }}>
              Serving
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <StatusDot tone={deployLive ? 'success' : 'neutral'} size={7} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {deployLive ? 'Serving' : 'Not deployed'}
              </Typography>
            </Stack>
          </Stack>
          <Typography
            variant="caption"
            onClick={go('serving')}
            sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
          >
            Open
          </Typography>
        </Stack>

        {deployLive ? (
          <Grid container spacing={2}>
            {[
              { label: 'Members scored', value: formatNumber(serving.inference_count || members) },
              { label: 'Last scored', value: lastScored || '—' },
              { label: 'Next score', value: serving.live !== false ? (nextRunLabel(serving.next_inference_at) || '—') : 'On demand' },
              { label: 'Model', value: model.label || '—' },
            ].map((f) => (
              <Grid item xs={6} sm={3} key={f.label}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{f.label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25 }} noWrap>
                  {f.value}
                </Typography>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Not serving yet
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {STATE_LABEL[modelStateKey] || 'Waiting'}. The first score lands after the bake-off.
              </Typography>
            </Box>
            <Typography
              variant="caption"
              onClick={go('models')}
              sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 700, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 0.25, '&:hover': { textDecoration: 'underline' } }}
            >
              Review bake-off <NorthEastIcon sx={{ fontSize: 13 }} />
            </Typography>
          </Stack>
        )}
      </Surface>
    </Stack>
  );
};

export default FleetDashboard;
